<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dropdown as VDropdown } from 'floating-vue';
import Icon from 'next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import EventStatusDropdown from '../EventStatusDropdown.vue';
import EventLabelDropdown from '../EventLabelDropdown.vue';
import StatusColorPicker from './StatusColorPicker.vue';
import { parseEventBorderColor } from 'dashboard/components-next/planning/Scheduler/allocationCategories';
import { resolveEventAccentColor } from '../useEventStageColors';

const props = defineProps({
  event: { type: Object, required: true },
  editingName: { type: Boolean, default: false },
  tempName: { type: String, default: '' },
  saving: { type: Boolean, default: false },
  statusOptions: { type: Array, default: () => [] },
  eventLabels: { type: Array, default: () => [] },
  viewers: { type: Array, default: () => [] },
  readOnly: { type: Boolean, default: false },
  canDelete: { type: Boolean, default: true },
  canChangeStage: { type: Boolean, default: true },
  canSellToNetwork: { type: Boolean, default: false },
});

const emit = defineEmits([
  'startEditName',
  'saveName',
  'cancelEditName',
  'update:tempName',
  'openHistory',
  'openShare',
  'copyMarkdown',
  'print',
  'delete',
  'editAllocations',
  'markRead',
  'markUnread',
  'updateStage',
  'updateEventLabel',
  'updateColor',
  'reschedule',
  'duplicate',
  'sellToNetwork',
]);

const { t } = useI18n();
const isActionsMenuOpen = ref(false);
const isColorPickerOpen = ref(false);
const nameInputRef = ref(null);

const onEditNameKeydown = event => {
  if (event.key !== 'Escape') return;
  event.preventDefault();
  emit('cancelEditName');
};

watch(
  () => props.editingName,
  async editing => {
    if (editing) {
      document.addEventListener('keydown', onEditNameKeydown);
      await nextTick();
      nameInputRef.value?.focus();
      return;
    }
    document.removeEventListener('keydown', onEditNameKeydown);
  }
);

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEditNameKeydown);
});

const accentColor = computed(() => resolveEventAccentColor(props.event));
const overrideColor = computed(() =>
  parseEventBorderColor(props.event?.metadata)
);

const actionButtonClass =
  'flex items-center justify-center size-10 rounded-full text-n-slate-11 hover:bg-white';

const menuItems = computed(() => {
  const items = [];

  if (!props.readOnly) {
    items.push(
      props.event.unread
        ? {
            key: 'markRead',
            label: t('PLANNING.EVENTS_CARD.MARK_AS_READ'),
            icon: 'i-lucide-check',
          }
        : {
            key: 'markUnread',
            label: t('PLANNING.EVENTS_CARD.MARK_AS_UNREAD'),
            icon: 'i-lucide-check',
          }
    );
  }

  items.push({
    key: 'print',
    label: t('PLANNING.SHOW.PRINT'),
    icon: 'i-lucide-printer',
  });

  items.push({
    key: 'openHistory',
    label: t('PLANNING.SHOW.HISTORY'),
    icon: 'i-lucide-history',
  });

  if (!props.readOnly) {
    items.push(
      {
        key: 'reschedule',
        label: t('PLANNING.SHOW.RESCHEDULE'),
        icon: 'i-lucide-calendar-clock',
      },
      {
        key: 'duplicate',
        label: t('PLANNING.SHOW.DUPLICATE'),
        icon: 'i-lucide-copy-plus',
      },
      {
        key: 'copyMarkdown',
        label: t('PLANNING.SHOW.COPY_MARKDOWN'),
        icon: 'i-lucide-copy',
      },
      {
        key: 'openShare',
        label: t('PLANNING.SHOW.SHARE_EVENT'),
        icon: 'i-lucide-share-2',
      }
    );
  }

  if (!props.readOnly && props.canSellToNetwork) {
    items.push({
      key: 'sellToNetwork',
      label: t('LEAD_NETWORK.SELL.ACTION'),
      icon: 'i-lucide-network',
    });
  }

  return items;
});

const toggleReadState = () => {
  if (props.event.unread) {
    emit('markRead');
  } else {
    emit('markUnread');
  }
};

const handleMenuAction = key => {
  isActionsMenuOpen.value = false;
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
  // Let the dropdown finish hiding before parent opens a modal.
  window.setTimeout(() => emit(key), 0);
};
</script>

<template>
  <div
    class="relative px-8 py-4 flex items-center gap-6 bg-n-surface-1 shrink-0 border-b border-n-slate-5 print:hidden"
  >
    <div class="flex w-full max-w-7xl mx-auto gap-4 print:hidden items-center">
      <span
        class="flex shrink-0 items-center justify-center self-stretch"
      >
        <span
          class="shrink-0 w-2 self-stretch rounded-full"
          :style="{ backgroundColor: accentColor }"
        />
      </span>

      <div class="flex flex-col min-w-0 flex-1">
        <div class="flex items-center gap-2.5 min-w-0">
          <div
            v-if="editingName"
            class="flex items-center gap-2 min-w-0 flex-1"
          >
            <input
              ref="nameInputRef"
              :value="tempName"
              type="text"
              class="reset-base no-margin text-xl font-bold text-n-slate-12 bg-transparent focus:outline-none min-w-0 flex-1 px-1 py-[9px]"
              @input="emit('update:tempName', $event.target.value)"
              @keydown.enter="emit('saveName')"
              @keydown.escape="emit('cancelEditName')"
            />
            <button
              type="button"
              class="flex p-2 text-woot-600 hover:text-woot-700"
              :disabled="saving"
              @click="emit('saveName')"
            >
              <Icon icon="i-lucide-check" class="size-5" />
            </button>
            <button
              type="button"
              class="flex p-2 text-n-slate-10 hover:text-n-slate-12"
              @click="emit('cancelEditName')"
            >
              <Icon icon="i-lucide-x" class="size-5" />
            </button>
          </div>
          <h1
            v-else
            class="text-xl font-bold text-n-slate-12 truncate mb-0 min-w-0"
            :class="{
              'cursor-pointer hover:text-woot-600 transition-colors': !readOnly,
            }"
            @click="!readOnly && emit('startEditName')"
          >
            {{ event.name }}
          </h1>

          <div class="shrink-0" @click.stop @mousedown.stop>
            <EventLabelDropdown
              :model-value="event.event_label_id"
              :labels="eventLabels"
              variant="dot"
              :disabled="readOnly"
              @update:model-value="emit('updateEventLabel', $event)"
            />
          </div>
        </div>
        <p
          v-if="!readOnly && !editingName"
          class="text-xs text-n-slate-10 mb-0 mt-0.5 truncate cursor-pointer"
          @click="emit('startEditName')"
        >
          {{ t('PLANNING.SHOW.CLICK_TO_EDIT_TITLE') }}
        </p>
      </div>

      <div v-if="viewers.length" class="flex items-center -space-x-2 shrink-0">
        <VDropdown
          v-for="viewer in viewers.slice(0, 5)"
          :key="viewer.id"
          :triggers="['hover']"
          :delay="{ show: 200, hide: 100 }"
          placement="bottom"
          :distance="8"
        >
          <div
            class="relative ring-2 ring-white dark:ring-slate-900 rounded-full cursor-pointer"
          >
            <Avatar
              :src="viewer.thumbnail"
              :name="viewer.name"
              :size="28"
              rounded-full
            />
            <span
              class="absolute -bottom-0.5 -right-0.5 block w-2.5 h-2.5 rounded-full bg-green-400 ring-2 ring-white dark:ring-slate-900"
            />
          </div>
          <template #popper>
            <div class="p-3 w-56">
              <div class="flex items-center gap-3 mb-2">
                <Avatar
                  :src="viewer.thumbnail"
                  :name="viewer.name"
                  :size="36"
                  rounded-full
                />
                <div class="min-w-0">
                  <p
                    class="text-sm font-semibold text-slate-900 dark:text-white truncate"
                  >
                    {{ viewer.name }}
                  </p>
                  <p
                    class="text-xs text-slate-500 dark:text-slate-400 truncate"
                  >
                    {{ viewer.email }}
                  </p>
                </div>
              </div>
              <div
                v-if="viewer.teams && viewer.teams.length"
                class="flex items-center gap-1.5 mb-2"
              >
                <Icon
                  icon="i-lucide-users"
                  class="size-3.5 text-slate-400 shrink-0"
                />
                <span
                  class="text-xs text-slate-600 dark:text-slate-300 truncate"
                >
                  {{ viewer.teams.join(', ') }}
                </span>
              </div>
              <div
                class="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400"
              >
                <Icon icon="i-lucide-eye" class="size-3.5 shrink-0" />
                <span>{{ t('PLANNING.SHOW.VIEWING_THIS_PAGE') }}</span>
              </div>
            </div>
          </template>
        </VDropdown>
        <div
          v-if="viewers.length > 5"
          class="flex items-center justify-center w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 ring-2 ring-white dark:ring-slate-900 text-xs font-medium text-slate-600 dark:text-slate-300"
        >
          +{{ viewers.length - 5 }}
        </div>
      </div>

      <div class="flex items-center shrink-0 rounded-full bg-n-slate-3 p-[2px]">
        <VDropdown
          v-model:shown="isActionsMenuOpen"
          :triggers="['click']"
          placement="bottom-end"
          :distance="8"
          :arrow="false"
          popper-class="event-menu-popper"
        >
          <button
            type="button"
            :class="[actionButtonClass, { 'bg-n-slate-4': isActionsMenuOpen }]"
            :aria-label="t('PLANNING.ALLOCATIONS.MORE_OPTIONS')"
          >
            <Icon icon="i-lucide-ellipsis" class="size-5" />
          </button>
          <template #popper>
            <div
              class="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-75 dark:border-slate-800 p-1.5 flex flex-col min-w-52"
              @click.stop
              @mousedown.stop
            >
              <button
                v-for="item in menuItems"
                :key="item.key"
                type="button"
                class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                @click="handleMenuAction(item.key)"
              >
                <Icon :icon="item.icon" class="size-5 text-n-slate-11" />
                <span>{{ item.label }}</span>
              </button>
              <button
                v-if="!readOnly && canDelete"
                type="button"
                class="w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2.5 text-red-600"
                @click="handleMenuAction('delete')"
              >
                <Icon icon="i-lucide-trash-2" class="size-5" />
                <span>{{ t('PLANNING.EVENTS.DELETE') }}</span>
              </button>
            </div>
          </template>
        </VDropdown>

        <VDropdown
          v-if="!readOnly"
          v-model:shown="isColorPickerOpen"
          :triggers="['click']"
          placement="bottom-end"
          :distance="8"
          :arrow="false"
          popper-class="event-menu-popper"
        >
          <button
            type="button"
            :class="[actionButtonClass, { 'bg-n-slate-4': isColorPickerOpen }]"
            :title="t('PLANNING.SHOW.EVENT_BORDER_COLOR')"
            :aria-label="t('PLANNING.SHOW.EVENT_BORDER_COLOR')"
          >
            <Icon
              icon="i-lucide-paint-bucket"
              class="size-5"
              :style="overrideColor ? { color: overrideColor } : undefined"
            />
          </button>
          <template #popper>
            <div
              class="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-75 dark:border-slate-800 p-3"
              @click.stop
              @mousedown.stop
            >
              <StatusColorPicker
                :model-value="overrideColor"
                :fallback-color="event.status_color"
                :reset-label="t('PLANNING.SHOW.EVENT_BORDER_COLOR_CLEAR')"
                @update:model-value="emit('updateColor', $event)"
              />
            </div>
          </template>
        </VDropdown>

        <button
          v-if="!readOnly"
          type="button"
          :class="actionButtonClass"
          :title="t('PLANNING.EVENTS.PLAN')"
          @click="emit('editAllocations')"
        >
          <Icon icon="i-lucide-calendar-check" class="size-5" />
        </button>
      </div>

      <EventStatusDropdown
        :model-value="event.system_stage"
        :custom-status="event.custom_status"
        :status-options="statusOptions"
        :accent-color="accentColor"
        :disabled="readOnly || !canChangeStage"
        size="md"
        @update:model-value="emit('updateStage', $event)"
      />

      <button
        v-if="!readOnly"
        v-tooltip.bottom="
          event.unread
            ? t('PLANNING.EVENTS_CARD.MARK_AS_READ')
            : t('PLANNING.EVENTS_CARD.MARK_AS_UNREAD')
        "
        type="button"
        class="shrink-0 flex items-center justify-center size-10 rounded-full text-white hover:opacity-90 transition-opacity"
        :style="{ backgroundColor: accentColor }"
        :aria-label="
          event.unread
            ? t('PLANNING.EVENTS_CARD.UNREAD')
            : t('PLANNING.EVENTS_CARD.READ')
        "
        @click="toggleReadState"
      >
        <Icon
          :icon="
            event.unread ? 'i-lucide-bell-dot animate-pulse' : 'i-lucide-bell'
          "
          class="size-5"
        />
      </button>
    </div>
  </div>
</template>
